import { useCallback, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';

/*
  ThemeProvider — управляет атрибутом data-theme на <html>.
  Только light/dark, без system. По умолчанию — тёмная (решение Максима 2026-10-20; с 2026-05-21
  по умолчанию была светлая). Выбор, сделанный переключателем, запоминается и важнее умолчания.
  index.html сразу ставит data-theme="dark", чтобы первая отрисовка до React была в тёмной теме.
*/

import { ThemeContext, type ThemeMode } from './context';
export type { ThemeMode } from './context';

const STORAGE_KEY = 'kaname_theme';

function readStored(): ThemeMode {
  if (typeof window === 'undefined') return 'dark';
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === 'light' ? 'light' : 'dark';
}

function applyToHTML(mode: ThemeMode) {
  const root = document.documentElement;
  root.setAttribute('data-theme', mode);
  root.style.colorScheme = mode;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => readStored());

  // До отрисовки: тот, кто выбрал светлую, не увидит мелькания тёмной.
  useLayoutEffect(() => {
    applyToHTML(mode);
  }, [mode]);

  const setMode = useCallback((m: ThemeMode) => {
    window.localStorage.setItem(STORAGE_KEY, m);
    setModeState(m);
  }, []);

  const toggle = useCallback(() => {
    setMode(mode === 'light' ? 'dark' : 'light');
  }, [mode, setMode]);

  const value = useMemo(() => ({ mode, toggle, setMode }), [mode, toggle, setMode]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
